import { useCallback, useEffect, useRef, useState } from "react";
import { CLOSE, OPEN, SHOW_MENU, SHOW_MOBILE_POPOVER } from "../constants/headerMenu";
import { useDeviceType } from "../deviceDetection";
import { usePathname } from "next/navigation";
import { Switch } from "@/utils/switch";

export const useNavBarCategoriesMenu = () => {
    
    const {isMobile , isTablet} = useDeviceType();
    const pathName = usePathname();

    const [open, setOpen] = useState(false);
    const [activeMenu, setActiveMenu] = useState(-1);
    const [showMobilePopOver, setMobilePopOver] = useState(isMobile ? true : false);

    const handleLevelNavigation = (action: string) => {
        switch (action) {
            case SHOW_MOBILE_POPOVER:
                setMobilePopOver(true);
                break;
            case SHOW_MENU:
                setMobilePopOver(false);
                setActiveMenu(-1);
                break;
            case CLOSE:
                setActiveMenu(-1);
                setOpen(false);
                setMobilePopOver(isMobile ? true : false);
                break;
            case OPEN:
                setOpen(true);
                break;
            default:
                break;
        }
    };

    const handleToolTip = useCallback(
        (action?: string) => () =>
            setOpen((open) =>
                Switch(action)
                    .case('open', () => true)
                    .case('close', () => false)
                    .defaultTo(() => !open)
            ),
        []
    );

    useEffect(() => handleLevelNavigation(CLOSE), [pathName]);

    
    // Keyboard Accessibility changes - START

    const topMenuListRef = useRef<HTMLUListElement | null>(null);
    const subMenuRefs = useRef<Record<number, HTMLUListElement | null>>({});

    const handledKeys = new Set([
        'ArrowDown',
        'ArrowUp',
        'ArrowLeft',
        'ArrowRight',
        'Enter',
        ' ',
        'Home',
        'End',
    ]);

    const handleMenuKeyDown = (e: React.KeyboardEvent) => {
       
        // Only intercept the keys we care about
        if (!handledKeys.has(e.key)) return;

        // Prevent default browser behaviour and stop bubbling so parent handlers don't also run
        e.preventDefault();
        e.stopPropagation();

        const target = (e.target as HTMLElement).closest('[role="menuitem"]') as HTMLElement | null;
        if (!target) return;

        const parent = findOneLevelParentUl(target);
        if (!parent) return;

        const directChildren = getMenuItemsFromTree(parent);
        
        const idx = directChildren.indexOf(target);
        if (idx === -1) return;

        const focusSibling = (nextIndex: number) => {
            const el = directChildren[nextIndex] as HTMLElement | undefined;
            if (!el) return;
            const focusable = (el.querySelector<HTMLElement>('a, button, [tabindex]:not([tabindex="-1"])') ?? el) as HTMLElement;
            focusable.focus();
        };

        switch (e.key) {
            case 'ArrowDown':
                focusSibling((idx + 1) % directChildren.length);
                break;
            case 'ArrowUp':
                focusSibling((idx - 1 + directChildren.length) % directChildren.length);
                break;
            case 'ArrowRight':
                if (target.getAttribute('aria-haspopup') === 'true') {
                    const topIndexAttr = target.dataset.index;
                    const topIndex = topIndexAttr ? Number(topIndexAttr) : idx;
                    setActiveMenu(topIndex);
                    setTimeout(() => focusFirstInSubmenu(topIndex), 0);
                } else {
                    focusSibling((idx + 1) % directChildren.length);
                }
                break;
            case 'ArrowLeft':
                {
                    const subRoot = (e.currentTarget as HTMLElement).closest('[data-submenu-index]') as HTMLElement | null;
                    if (subRoot) {
                        const parentIndex = Number(subRoot.dataset.submenuIndex);
                        setActiveMenu(-1);
                        const topItem = topMenuListRef.current?.querySelector<HTMLElement>(`[data-index="${parentIndex}"]`);
                        topItem?.focus();
                    }
                }
                break;
            case 'Enter':
            case ' ':
                (target.querySelector<HTMLElement>('a, button') ?? target)?.click();
                if (target.getAttribute('aria-haspopup') === 'true') {
                    const topIndexAttr = target.dataset.index;
                    const topIndex = topIndexAttr ? Number(topIndexAttr) : idx;
                    setActiveMenu(topIndex);
                    setTimeout(() => focusFirstInSubmenu(topIndex), 0);
                }
                break;
            case 'Home':
                focusSibling(0);
                break;
            case 'End':
                focusSibling(directChildren.length - 1);
                break;
        }
    };

    // helper: focus first focusable menuitem inside a submenu
    const focusFirstInSubmenu = (idx: number) => {
        const sub = subMenuRefs.current[idx];
        if (!sub) return;
        const first = sub.querySelector<HTMLElement>('[role="menuitem"]');
        first?.focus();
    };

    useEffect(() => { // Focussing the first element in the manu on open
        if (open) {
            setTimeout(() => {
                document.querySelector<HTMLElement>('[id^="header-link-level1-"]')?.focus();
            }, 200)
        }
    }, [open]);

    // Keyboard Accessibility changes - END 

    return {
        open,
        showMobilePopOver,
        handleLevelNavigation,
        handleToolTip,
        isMobile,
        isTablet,
        pathName,
        topMenuListRef,
        subMenuRefs,
        activeMenu,
        setActiveMenu,
        handleMenuKeyDown,
        focusFirstInSubmenu
    }
}


const getMenuItemsFromTree = (parent: HTMLElement): HTMLElement[] => {

    const results: HTMLElement[] = [];

    Array.from(parent.children).forEach((child) => {
        if (!(child instanceof HTMLElement)) return;
        if (child.getAttribute('role') === 'menuitem') {
            results.push(child);
        }else if (child?.querySelector<HTMLElement>(".content-recomnedation-widget") !== null) {
            results.push(
                ...Array.from(
                    child.querySelectorAll<HTMLElement>('.content-recomnedation-widget > li > [role="menuitem"]')
                )
            );
        }else if (child?.querySelector<HTMLElement>("a, button")?.getAttribute('role') === 'menuitem') {
            results.push(child.querySelector<HTMLElement>("a, button") as HTMLElement);
        }
    });

    return results;
}

function findOneLevelParentUl(target: HTMLElement): HTMLUListElement | null {
    let current: HTMLElement | null = target;

    while (current) {
        if (current.tagName.toLowerCase() === 'li') {
            let ancestor = current.parentElement;
            while (ancestor) {
                if (ancestor.tagName.toLowerCase() === 'ul') {
                    return ancestor as HTMLUListElement;
                }
                ancestor = ancestor.parentElement;
            }
            // If no ul found above this li, stop or continue depending on your need
            // Here we choose to stop because only the closest li is considered
            return null;
        }
        current = current.parentElement;
    }
    return null;
}

